import Image from '@atoms/Image/Image';
import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import type { IImage } from '@models/IImage';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

interface Props {
  readonly title: string;
  readonly image: IImage;
  readonly hasTopPadding: boolean;
}

const LightScrollAnimation = ({ title, image, hasTopPadding }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const { scrollY } = useScroll({
    offset: ['start end', 'end start'],
    target: ref,
  });
  const isMobileView = useMediaQuery(767);
  const isTabletView = useMediaQuery(1024);
  const isDesktopView = useMediaQuery(1440);

  const isMobileScreen = useMediaQuery(767, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileView;

  const getScrollRange = () => {
    if (isMobileView) return [0, 130];
    if (isTabletView) return [0, 88];
    return [0, 170];
  };

  const getYPosition = () => {
    if (isMobileView || isTabletView) return [0, -55];
    return [0, -140];
  };

  const imageYOffset = useTransform(scrollY, getScrollRange(), getYPosition());

  const getFontResizeValues = () => {
    if (isMobileView) return ['38px', '35px'];
    if (isTabletView) return ['60px', '55px'];
    if (isDesktopView) return ['100px', '94px'];
    return ['127px', '115px'];
  };

  const fontSize = useTransform(
    scrollY,
    getScrollRange(),
    getFontResizeValues()
  );

  return (
    <section
      className={classNames(
        'component-padding w-full overflow-visible bg-black-100 pt-6 text-white',
        { 'md:pt-20': hasTopPadding, 'md:pt-0': !hasTopPadding }
      )}
      ref={ref}
    >
      <SoftLoadWrapper>
        <div className="container mx-auto flex h-full flex-col items-center gap-7 lg:gap-14">
          <MotionWrapper
            tag="h1"
            style={{
              fontSize,
              lineHeight: fontSize,
            }}
            dangerouslySetInnerHTML={{ __html: title }}
          />

          <MotionWrapper
            className="w-[90%] md:w-4/6 xl:w-5/12"
            style={{ y: isZoomedDesktop ? 0 : imageYOffset }}
          >
            <Image
              image={image}
              imageClasses="w-full object-contain"
              sizes="(min-width: 1024px) 1024px, 100vw"
            />
          </MotionWrapper>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default LightScrollAnimation;
